/**
 * Auth Context - Provides authentication functionality
 *
 * Both production and dev mode authenticate via an L500 token obtained from
 * the BFF /token endpoint. In production the token is minted from a validated
 * Frontegg JWT; in dev mode it's minted via the /token devMode path which
 * JIT-creates the user with staff.super_admin role. Either way, the user
 * identity and permissions come from the decoded L500 token.
 */
import {
  useAuth as useFronteggAuth,
  useAuthActions as useFronteggAuthActions,
} from "@frontegg/react";
import type { AuthState, AuthActions } from "@frontegg/redux-store";
import { createContext, useContext, useMemo, type ReactNode } from "react";
import {
  authTokenManager,
  decodeL500Token,
  usePermissionKeys as useSharedPermissionKeys,
} from "@legal-500/auth-client";
import { publicRuntimeConfig } from "../config/publicRuntimeConfig";

const isAuthDisabled = publicRuntimeConfig.authDisabled;

export type AuthContextType = AuthState;
type AuthActionsContextType = AuthActions;

const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: false,
} as AuthContextType);

const AuthActionsContext = createContext<AuthActionsContextType>({
  logout: () => {
    /* noop */
  },
} as AuthActionsContextType);

function buildAuthStateFromL500Token(): AuthContextType | null {
  const token = authTokenManager.getToken();
  if (!token) return null;

  const decoded = decodeL500Token(token);
  if (!decoded) return null;

  return {
    user: {
      id: decoded.sub,
      email: decoded.email,
      name: decoded.name,
      profilePictureUrl: null,
      tenantId: decoded.organisationId ?? "",
      permissions: [...decoded.permissions],
      roles: [],
      accessToken: token,
    },
    isAuthenticated: true,
    isLoading: false,
    tenantsState: {
      tenants: [],
      activeTenant: null,
    },
  } as unknown as AuthContextType;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  if (isAuthDisabled) {
    return <L500TokenAuthProvider>{children}</L500TokenAuthProvider>;
  }

  return <FronteggAuthProvider>{children}</FronteggAuthProvider>;
}

function L500TokenAuthProvider({ children }: { children: ReactNode }) {
  const authValue = useMemo(() => {
    const state = buildAuthStateFromL500Token();
    return (
      state ??
      ({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      } as AuthContextType)
    );
  }, []);

  const authActions = useMemo(
    () =>
      ({
        logout: async () => {
          authTokenManager.clearToken();
          window.location.reload();
        },
      }) as unknown as AuthActionsContextType,
    [],
  );

  return (
    <AuthContext.Provider value={authValue}>
      <AuthActionsContext.Provider value={authActions}>{children}</AuthActionsContext.Provider>
    </AuthContext.Provider>
  );
}

/**
 * Frontegg auth provider - delegates to Frontegg hooks
 * Only rendered when FronteggProvider is mounted
 */
function FronteggAuthProvider({ children }: { children: ReactNode }) {
  const authData = useFronteggAuth();
  const authActions = useFronteggAuthActions();

  return (
    <AuthContext.Provider value={authData}>
      <AuthActionsContext.Provider value={authActions}>{children}</AuthActionsContext.Provider>
    </AuthContext.Provider>
  );
}

/**
 * Helper to extract permission keys from user object
 * Handles both string and object formats, filters out UUIDs
 */
export function extractPermissionKeys(user: AuthContextType["user"]): string[] {
  const permissions = new Set<string>();
  const isUUID = (str: string) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);

  // Extract from top-level permissions
  if (user?.permissions) {
    for (const p of user.permissions as unknown[]) {
      const key = typeof p === "string" ? p : (p as { key: string }).key;
      if (key && !isUUID(key)) {
        permissions.add(key);
      }
    }
  }

  // Extract from role permissions
  if (user?.roles) {
    for (const role of user.roles as Array<{ permissions?: unknown[] }>) {
      if (role.permissions) {
        for (const p of role.permissions) {
          const key = typeof p === "string" ? p : (p as { key: string }).key;
          if (key && !isUUID(key)) {
            permissions.add(key);
          }
        }
      }
    }
  }

  return Array.from(permissions);
}

/**
 * Access auth data (user, isAuthenticated, etc.)
 */
export function useAuth(): AuthContextType {
  return useContext(AuthContext);
}

/**
 * Access auth actions (logout, etc.)
 */
export function useAuthActions() {
  return useContext(AuthActionsContext);
}

/**
 * Return the authoritative platform permission keys for the current user.
 *
 * Prefers the decoded L500 token because that's the platform-signed set
 * of permissions derived from the user's platform roles (e.g.
 * staff.super_admin). Falls back to the user object's permissions list
 * only when no L500 token is present (e.g. the pre-exchange moment on
 * first mount, or an auth setup that hasn't wired token exchange yet).
 */
export function usePermissionKeys(): string[] {
  const { user } = useAuth();
  const fromToken = useSharedPermissionKeys();
  return useMemo(
    () => (fromToken.length > 0 ? fromToken : extractPermissionKeys(user)),
    [fromToken, user],
  );
}
